"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { SigmaVueLogo } from "@/components/brand/sigmavue-logo";
interface AuthShellProps {
  children: ReactNode;
  activeTab?: "signin" | "signup";
  title?: string;
  description?: string;
  secondaryActionHref?: string;
  secondaryActionLabel?: string;
}

// SIGNED-OUT MARKETING ILLUSTRATION — NOT LIVE DATA.
// This rail renders on /login, /purchase-success and other signed-out auth
// screens, where no user session and therefore no real desk feed exists.
// It previously carried invented executions ("filled 2 ES @ 5871.75") with
// frozen "N sec ago" literals under a pulsing "Live agent activity" header —
// a fabricated fill price presented as live on a futures product, which is
// exactly what root AGENTS.md #6 and the frontend live-data-only rule forbid,
// on the first screen a paying member sees after checkout.
// Rules for this array: describe what each agent DOES, never a price, a fill,
// a quantity, a P&L, or a timestamp. Anything time- or money-specific must
// come from the real feed behind auth.
const FEED_ROWS = [{
  agent: "Carter",
  image: "roster-carter.webp",
  text: "reads price action for continuation setups",
  tone: "mint"
}, {
  agent: "Wags",
  image: "roster-wags.webp",
  text: "checks prop-firm drawdown headroom before every entry",
  tone: "ok"
}, {
  agent: "Sigma",
  image: "roster-sigma.webp",
  text: "orchestrates the agent council on each decision",
  tone: "mint"
}, {
  agent: "Taylor",
  image: "roster-taylor.webp",
  text: "routes execution and mirrors it across your accounts",
  tone: "ok"
}, {
  agent: "Wendy",
  image: "roster-wendy.webp",
  text: "manages trailing stops as a position works",
  tone: "warm"
}] as const;
function feedToneClass(tone: (typeof FEED_ROWS)[number]["tone"]) {
  if (tone === "ok") return "border-green-400/35 bg-green-400/10";
  if (tone === "warm") return "border-[#cc785c]/35 bg-[#cc785c]/10";
  return "border-[#00ffcc]/30 bg-[#00ffcc]/10";
}
export function AuthShell({
  children,
  title,
  description
}: AuthShellProps) {
  return <div className="auth-shell relative grid min-h-[100dvh] grid-cols-1 overflow-hidden bg-[radial-gradient(120%_90%_at_12%_-10%,rgba(0,255,204,.06),transparent_58%),#090c12] text-white min-[880px]:grid-cols-[1.02fr_.98fr]" data-sentry-component="AuthShell" data-sentry-source-file="auth-shell.tsx">
      <aside className="relative hidden flex-col justify-center gap-[30px] overflow-hidden border-r border-white/5 px-[clamp(40px,5vw,80px)] pb-16 pt-24 min-[880px]:flex">
        <div className="auth-rail-watermark pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[min(520px,70vw)] opacity-[0.105] mix-blend-screen grayscale brightness-[1.75] contrast-[1.08]">
          <Image src="/images/sigmavue-logo-metallic-transparent.webp" alt="" fill priority sizes="520px" className="object-contain" data-sentry-element="Image" data-sentry-source-file="auth-shell.tsx" />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(72%_58%_at_38%_18%,rgba(0,255,204,.08),transparent_62%),linear-gradient(130deg,rgba(0,255,204,.035),transparent_42%)]" />

        <div className="relative z-10">
          <SigmaVueLogo href="/" showWordmark size="sm" priority wordmarkClassName="text-[17px] uppercase" markClassName="drop-shadow-[0_0_10px_rgba(255,255,255,0.22)]" ariaLabel="SigmaVue home" data-sentry-element="SigmaVueLogo" data-sentry-source-file="auth-shell.tsx" />
        </div>

        <div className="relative z-10 max-w-[520px]">
          <div className="inline-flex w-max items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#00ffcc]">
            {/* Brand eyebrow, not a liveness claim — nothing here is a feed. */}
            The trading floor
          </div>
          <h2 className="mt-5 max-w-[12ch] font-goldman text-[clamp(34px,4.2vw,56px)] font-bold leading-[1.02] tracking-normal text-white">
            Your AI hedge fund
            <br />
            is already <em className="not-italic text-[#00ffcc]">built.</em>
          </h2>
          {/* Copy honesty: the member path is Whop sign-in — the Whop account
              a buyer already has IS the login, and entitlement is read from
              their Whop membership. Do not reintroduce copy telling members to
              create a SigmaVue password; that path exists only for legacy and
              admin accounts and is not offered here. */}
          <p className="mt-5 max-w-[42ch] text-[15px] leading-7 text-white/58">
            Whop handles the account and the billing. SigmaVue handles the desk. One login, one subscription, your whole desk behind it.
          </p>
        </div>

        <div className="relative z-10 flex h-[228px] max-w-[520px] flex-col overflow-hidden rounded-[18px] border border-white/[0.06] bg-white/[0.045] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,.08),0_18px_70px_rgba(0,0,0,.35)] backdrop-blur-xl">
          <div className="mb-3 flex shrink-0 items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/42">
            {/* No live dot: this rail is signed-out illustration, not a feed. */}
            What your desk does
          </div>
          {/* SIG-310: the authFeed track must clip inside its own viewport —
              when it shares the card's clip box with the header, translated
              rows slide up over the header text before exiting the card. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <div className="flex animate-[authFeed_30s_linear_infinite] flex-col gap-3">
              {(["a", "b"] as const).flatMap(cycle => FEED_ROWS.map(row => <div key={`feed-${cycle}:${row.agent}`} className="flex min-h-10 items-start gap-3">
                    <span className={`relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border ${feedToneClass(row.tone)}`}>
                      <Image src={`/award/${row.image}`} alt="" fill sizes="32px" className="object-cover object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11.5px] leading-5 text-white/58">
                        <b className="font-semibold text-white">{row.agent}</b>{" "}
                        {row.text}
                      </span>
                    </span>
                  </div>))}
            </div>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-[#090c12] to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 bg-gradient-to-t from-[#090c12] to-transparent" />
          </div>
        </div>

        {/* COMPLIANCE CLAIM — read before editing.
            "SOC 2 Type II" as a bare badge beside our own brand asserts that
            SIGMAVUE holds a Type II attestation: an independent auditor
            observed OUR controls over a 3-12 month window and issued a report.
            We have no such report. What is true, and what the public /faq
            already says, is that our INFRASTRUCTURE PROVIDERS (Render, Vercel,
            AWS) are SOC 2 compliant. Those are different claims, and the
            unqualified one was showing to paying members on /login, /register
            and /purchase-success. Do not restore the bare badge unless someone
            can produce the actual attestation report. */}
        <div className="relative z-10 font-mono text-[10.5px] leading-6 tracking-[0.06em] text-white/35">
          SOC 2-compliant infrastructure · Whop billing · read-only API access
          <br />
          213 PRDs · zero VC · built by a trader and an AI.
        </div>
      </aside>

      {/* Symmetric padding + true centering (operator-flagged: the card sat
          off-center with dead space below). The card column gets the same
          radial presence as the rail so the composition reads balanced.
          #281/#306 follow-up (Greptile P1): plain justify-center clips BOTH ends
          of an overflowing flex column on short viewports — the card's lower
          controls fall below the fold and the top half is unreachable by scroll.
          `safe center` (arbitrary property — Tailwind 3 has no *-safe utility)
          centers when there's room and top-aligns + scrolls when there isn't. */}
      <main className="relative flex min-h-[100dvh] flex-col items-center [justify-content:safe_center] px-6 py-14">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_44%_at_50%_50%,rgba(0,255,204,.045),transparent_70%)]" />
        <SigmaVueLogo href="/" showWordmark size="sm" priority wordmarkClassName="text-[17px] uppercase" markClassName="drop-shadow-[0_0_10px_rgba(255,255,255,0.22)]" ariaLabel="SigmaVue home" className="relative mb-8 justify-center min-[880px]:hidden" data-sentry-element="SigmaVueLogo" data-sentry-source-file="auth-shell.tsx" />
        {/* `title` / `description` were declared on AuthShellProps but never
            destructured, so every page passing them rendered them nowhere.
            /forgot-password, /auth/verify-email and /auth/reset-password each
            pass a title and their child components carry no heading of their
            own — those screens showed a bare form with nothing saying what it
            was for. verify-email is on the critical path the moment
            verification emails start sending.
            Callers that render their own <h1> (purchase-success-client) pass
            no title, so nothing double-renders. */}
        <div className="relative w-full max-w-[460px]">
          {(title || description) && <div className="mb-6 space-y-2 text-center">
              {title && <h1 className="font-goldman text-[26px] font-bold leading-tight text-white">
                  {title}
                </h1>}
              {description && <p className="text-[14px] leading-6 text-white/60">{description}</p>}
            </div>}
          {children}
        </div>
        {/* Same compliance rule as the rail above: infrastructure providers
            hold SOC 2, SigmaVue does not claim its own attestation. */}
        <p className="relative mt-7 text-center font-mono text-[9.5px] uppercase tracking-[0.22em] text-white/25">
          Whop billing · Whop sign-in · SOC 2-compliant infrastructure
        </p>
      </main>

      <style>{`
        @keyframes authFeed {
          to {
            transform: translateY(-50%);
          }
        }

        @keyframes authLogoCoin {
          0% {
            transform: translate(-50%, -50%) perspective(1200px) rotateY(0deg);
          }
          100% {
            transform: translate(-50%, -50%) perspective(1200px) rotateY(360deg);
          }
        }

        @keyframes authPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.35;
          }
        }

        .auth-rail-watermark {
          transform: translate(-50%, -50%) perspective(1200px) rotateY(0deg);
          transform-origin: center;
          animation: authLogoCoin 24s linear infinite;
        }

        .auth-feed-dot {
          animation: authPulse 1.6s infinite;
        }

        .auth-shell :focus-visible {
          outline: 2px solid #00ffcc;
          outline-offset: 3px;
          border-radius: 8px;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-\\[authFeed_30s_linear_infinite\\],
          .auth-rail-watermark,
          .auth-feed-dot {
            animation: none;
          }
        }
      `}</style>
    </div>;
}